import React, { useEffect, useRef, useState } from 'react';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { DraggableTaskItem } from './DraggableTaskItem';
import { type Task } from '../../stores/TaskStore';

import { Paper } from '@mui/material';
import CustomMuiScrollBar from '../ui/ScrollBar';

interface DraggableTaskListProps {
  tasks: Task[];
  onToggleTask: (taskId: string) => void;
  onDeleteTask: (taskId: string) => void;
  onTogglePin: (taskId: string) => void;
  onReorderTasks: (taskIds: string[]) => void;
}

export const DraggableTaskList: React.FC<DraggableTaskListProps> = ({
  tasks,
  onToggleTask,
  onDeleteTask,
  onTogglePin,
  onReorderTasks,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    return dropTargetForElements({
      element,
      onDragEnter: () => setIsDraggingOver(true),
      onDragLeave: () => setIsDraggingOver(false),
      onDrop: ({ source, location }) => {
        setIsDraggingOver(false);
        const draggedTaskId = source.data.taskId as string;
        if (!draggedTaskId) return;

        const target = location.current.dropTargets[0];
        if (!target) return;

        // 简化拖拽逻辑：重新排列任务
        const allTaskElements = Array.from(element.children) as HTMLElement[];
        const draggedElement = allTaskElements.find(el =>
          el.getAttribute('data-task-id') === draggedTaskId
        );

        if (!draggedElement) return;

        // 获取当前任务顺序
        const currentOrder = tasks.map(task => task.id);
        const draggedIndex = currentOrder.indexOf(draggedTaskId);

        if (draggedIndex === -1) return;

        // 找到最接近的拖拽目标
        const { clientY } = location.current.input;
        let targetIndex = currentOrder.length;

        for (let i = 0; i < allTaskElements.length; i++) {
          const taskElement = allTaskElements[i];
          const rect = taskElement.getBoundingClientRect();
          const elementCenter = rect.top + rect.height / 2;

          if (clientY < elementCenter) {
            const taskId = taskElement.getAttribute('data-task-id');
            targetIndex = taskId ? currentOrder.indexOf(taskId) : i;
            break;
          }
        }

        // 如果拖拽到自己的位置，不做处理
        if (targetIndex === draggedIndex) return;

        // 重新排列数组
        const newOrder = [...currentOrder];
        const [removed] = newOrder.splice(draggedIndex, 1);

        // 调整目标索引（如果向后拖拽）
        const insertIndex = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;
        newOrder.splice(insertIndex, 0, removed);

        onReorderTasks(newOrder);
      },
    });
  }, [tasks, onReorderTasks]);

  return (
    <Paper
      ref={ref}
      className={`h-full ${isDraggingOver
        ? 'drag-active'
        : ''
        }`}
      sx={{
        border: '4px solid var(--color-border)',
        borderRadius: '16px',
        boxShadow: 'none',
        height: 'calc(100% - 16px)', // 减去父容器padding，自动适应
        overflowY: 'auto',
        backgroundColor: 'var(--color-task-manager-bg)',
        transition: 'background-color 0.3s ease',
        ...CustomMuiScrollBar()
      }}
    >
      {tasks.map((task) => {
        const taskTags = task.tags || [];
        return (
          <div
            key={task.id}
            data-task-id={task.id}
            className="relative transition-all duration-300 ease-out p-1"
          >
            <DraggableTaskItem
              task={task}
              taskTags={taskTags}
              onToggle={onToggleTask}
              onDelete={onDeleteTask}
              onTogglePin={onTogglePin}
            />
          </div>
        );
      })}
    </Paper>
  );
};
